import { extractRowFileFields, isRowList, multipleFileFieldNames } from './fileFields';
import type { ApplicationConfigType, ApplicationItemType, FileType, FormDataType } from './types';

/**
 * Radfeltene for søknaden: fra den eksakte versjonerte malen, gjeldende mal,
 * og — hvis ingen av dem kjenner feltet — dataen selv (en radliste i payload
 * med filer i attachments på samme nøkkel).
 */
export function rowFileFieldNames(
    application: ApplicationItemType | null,
    applicationConfig: ApplicationConfigType | null,
): Set<string> {
    const names = new Set<string>([
        ...extractRowFileFields(application?.applicationTemplate).map((f) => f.name),
        ...extractRowFileFields(applicationConfig).map((f) => f.name),
    ]);
    const payload = (application?.payload || {}) as Record<string, unknown>;
    for (const key of Object.keys(application?.attachments || {})) {
        if (isRowList(payload[key])) names.add(key);
    }
    return names;
}

/**
 * Setter sammen visningsdataene for en søknad — det samme datasettet som
 * `Accordion` rendrer i «Søknadsinfo».
 *
 * Sammensetningen er felles for skjermvisning, utskrift, PDF og ZIP-pakking.
 * Poenget er at eksportene bygger på NØYAKTIG samme grunnlag som det søker
 * ser i portalen: da kan ingen eksport ved et uhell lekke felt søker ikke
 * har tilgang til (vurderinger, tidligere søknader, interne notater — alt
 * det som ligger i admin sin `getPdfData` og ikke her).
 *
 * Fil-felt håndteres etter malens elementtype:
 *   - `fileupload` (multiple)        → ALLE distinkte filer beholdes
 *   - `fileuploadsingle` (versjonert) → reduseres til nyeste versjon
 */
export function buildApplicationFormData(
    application: ApplicationItemType | null,
    applicationConfig: ApplicationConfigType | null,
): FormDataType {
    if (!application) return {} as FormDataType;

    const payload = application.payload || {};
    const attachments: Record<string, FileType[]> = application.attachments || {};
    const media: Record<string, FileType[]> = application.media || {};

    const mediaFormData = Object.entries(media).reduce<Record<string, FileType[]>>(
        (acc, [key, files]) => {
            if (Array.isArray(files) && files.length > 0) {
                acc[key] = files;
            }
            return acc;
        },
        {},
    );

    const multipleFields = multipleFileFieldNames(applicationConfig);
    // Radbilag hører til radene i payload, som allerede har referansen og
    // lenken fra API-et. Uten dette ble hele utleggstabellen erstattet av
    // første bilag, siden tabellen ikke er et `fileupload`-felt.
    const rowFields = rowFileFieldNames(application, applicationConfig);

    return {
        ...payload,
        ...mediaFormData,
        ...Object.entries(attachments).reduce<Record<string, FileType | FileType[]>>(
            (acc, [label, files]) => {
                if (rowFields.has(label)) return acc;
                if (Array.isArray(files) && files.length > 0) {
                    if (multipleFields.has(label)) {
                        acc[label] = files;
                    } else {
                        acc[label] = files.reduce((prev, curr) =>
                            (curr.version || 0) > (prev.version || 0) ? curr : prev,
                        );
                    }
                }
                return acc;
            },
            {},
        ),
    } as FormDataType;
}

/**
 * Flat liste over vedleggene søker faktisk kan se — brukt av ZIP-pakkingen.
 *
 * Følger samme versjonsregel som `buildApplicationFormData`, men returnerer
 * filene med et forslag til filnavn i arkivet. Filer uten `download_url`
 * (f.eks. rader som ennå ikke er lastet opp ferdig) hoppes over.
 */
export type ExportFile = { name: string; url: string; field: string };

export function collectApplicationFiles(
    application: ApplicationItemType | null,
    applicationConfig: ApplicationConfigType | null,
): ExportFile[] {
    if (!application) return [];

    // Radbilag er distinkte filer (én per rad), ikke versjoner av én fil.
    const multipleFields = new Set([
        ...multipleFileFieldNames(applicationConfig),
        ...rowFileFieldNames(application, applicationConfig),
    ]);
    const out: ExportFile[] = [];
    const seen = new Set<string>();

    const push = (field: string, file: FileType | undefined) => {
        if (!file?.download_url || !file.name) return;
        if (seen.has(file.download_url)) return;
        seen.add(file.download_url);
        out.push({ name: file.name, url: file.download_url, field });
    };

    for (const [field, files] of Object.entries(application.attachments || {})) {
        if (!Array.isArray(files) || files.length === 0) continue;
        if (multipleFields.has(field)) {
            files.forEach((file) => push(field, file));
        } else {
            push(
                field,
                files.reduce((prev, curr) => ((curr.version || 0) > (prev.version || 0) ? curr : prev)),
            );
        }
    }

    for (const [field, files] of Object.entries(application.media || {})) {
        if (!Array.isArray(files)) continue;
        files.forEach((file) => push(field, file));
    }

    return out;
}
